import { forgetActiveCompany } from "@/app/auth/helpers";
import { ColorModeButton } from "@/components/ui/color-mode";
import {
  Card,
  Link,
  Text,
  Alert,
  Button,
  Image,
  HStack,
  VStack,
  Separator,
} from "@chakra-ui/react";

import { config } from "@app/config/app";
import { UserPersona } from "@shared/ui/user-persona";
import { LogoutButton } from "@features/auth/log-out";
import { DestinationButton } from "./destination-button";
import { useDestinations } from "../lib/use-destinations";
import { CreateOrderModal } from "@features/order/create";
import { CompanyContextSwitch } from "@widgets/company-context-switch/ui";
import { useAuthenticationContext } from "@app/auth/use-authentication-context";

export const SideBar: React.FC = () => {
  const {
    user,
    lastName,
    firstName,
    activeCompany,
    setActiveCompany,
    isSuperuserActingBehalfOfCompany,
  } = useAuthenticationContext();

  const availableDestinations = useDestinations();

  return (
    <VStack
      w="full"
      h="calc(95vh)"
      p={4}
      rounded="md"
      justifyContent="space-between"
      overflowY="auto"
      style={{ scrollbarWidth: "thin" }}
    >
      <VStack w="full">
        <HStack gap={2}>
          <Image src="/sas.svg" alt="Logo" w="1.5rem" />
          <Text fontSize="md" color="teal.500">
            Customer Portal
          </Text>
        </HStack>

        <Separator w="full" orientation="horizontal" my={2} />

        <UserPersona
          email={user?.email ?? ""}
          name={`${firstName} ${lastName}`.trim() ?? "Guest"}
          superuser={user?.isSuperuser ?? false}
        />

        <Separator w="full" orientation="horizontal" my={2} />

        {isSuperuserActingBehalfOfCompany && (
          <VStack w="full">
            <Alert.Root status="warning" size="sm" as={VStack}>
              <Alert.Title>
                All operations are performed on behalf of the manager in{" "}
                {activeCompany?.name}.
              </Alert.Title>
              <Button
                w="full"
                size="xs"
                variant="solid"
                colorPalette="orange"
                onClick={() => {
                  setActiveCompany(undefined);
                  forgetActiveCompany();
                }}
              >
                Quit on behalf mode
              </Button>
            </Alert.Root>
            <Separator w="full" orientation="horizontal" my={2} />
          </VStack>
        )}

        {availableDestinations.map((destination) => (
          <DestinationButton key={destination.id} {...destination} />
        ))}

        <Separator w="full" orientation="horizontal" my={2} />

        <CreateOrderModal
          triggerProps={{ w: "full" }}
          buttonProps={{ w: "full" }}
        />

        <CompanyContextSwitch />

        {!user?.isSuperuser && (
          <Card.Root w="full" gap={0} p={4}>
            <Text fontSize="0.8rem" w="full" color="fg.muted">
              Contact us:
            </Text>
            <Link w="fit" href={`mailto:${config.APP_SUPPORT_EMAIL}`}>
              <Text fontSize="0.9rem" w="full">
                {config.APP_SUPPORT_EMAIL}
              </Text>
            </Link>
          </Card.Root>
        )}
      </VStack>

      <HStack w="full">
        <ColorModeButton variant="outline" />
        <LogoutButton size="md" variant="subtle" />
      </HStack>
    </VStack>
  );
};
